import { Grid, InputAdornment, Typography } from "@material-ui/core";
import { PlanToBuildReserveStatus, SocItem } from "../../../domain/SocItem";
import { SectionProps } from "../SectionProps";
import { Controller, useWatch } from "react-hook-form";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { fieldLabels } from "../fieldLabels";
import { Error } from "../../common/Error";
import { Select } from "@ofqual/portal-components/components/Select";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import {
  planToBuildReserveService,
  reserveThresholdPolicyService,
} from "../../../services";

export const QuestionThreeSection: React.FC<SectionProps<SocItem>> = (
  sectionProps
) => {
  const { control, disabled, errors } = sectionProps;
  const { planToBuildReserve } = useWatch<SocItem>({
    control,
    name: ["planToBuildReserve"],
  });

  const [plantToBuildReserveLookups, reserveThresholdLevelLookups] = useLookup(
    planToBuildReserveService,
    reserveThresholdPolicyService
  );

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="body1">
          Please provide details regarding your current cash reserve position.
          You should include.
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="monthlyFixedCost"
          rules={{ required: true, min: 0 }}
          as={
            <TextField
              type="number"
              required
              label={fieldLabels.monthlyFixedCost}
              readOnly={disabled}
              data-test="monthlyFixedCost-field"
              InputProps={{
                inputProps: { min: 0, pattern: "[0-9]*" },
                startAdornment: (
                  <InputAdornment position="start">£</InputAdornment>
                ),
              }}
            />
          }
        />
        {errors.monthlyFixedCost?.type === "required" && (
          <Error data-test="monthlyFixedCost-validation">
            This field is required
          </Error>
        )}
        {errors.monthlyFixedCost?.type === "min" && (
          <Error data-test="monthlyFixedCost-validation">
            Must be greater than 0
          </Error>
        )}
        {errors.monthlyFixedCost?.type === "pattern" && (
          <Error data-test="monthlyFixedCost-validation">
            {errors.monthlyFixedCost.message}
          </Error>
        )}
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="monthlyCoverage"
          rules={{ required: true, min: 0 }}
          as={
            <TextField
              type="number"
              required
              label={fieldLabels.monthlyCoverage}
              readOnly={disabled}
              data-test="monthlyCoverage-field"
              InputProps={{ inputProps: { min: 0, pattern: "[0-9]*" } }}
            />
          }
        />
        {errors.monthlyCoverage?.type === "required" && (
          <Error data-test="monthlyCoverage-validation">
            This field is required
          </Error>
        )}
        {errors.monthlyCoverage?.type === "min" && (
          <Error data-test="monthlyCoverage-validation">
            Must be greater than 0
          </Error>
        )}
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="reserveThresholdLevel"
          as={
            <Select
              label={fieldLabels.reserveThresholdLevel}
              datatest="reserveThreshold-field"
              disabled={disabled}
            >
              {reserveThresholdLevelLookups}
            </Select>
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="planToBuildReserve"
          as={
            <Select
              label={fieldLabels.planToBuildReserve}
              datatest="planToBuildReserve-field"
              disabled={disabled}
            >
              {plantToBuildReserveLookups}
            </Select>
          }
        />
      </Grid>
      {planToBuildReserve === PlanToBuildReserveStatus.Yes && (
        <Grid item xs={12} sm={12}>
          <Controller
            control={control}
            rules={{ maxLength: 5000 }}
            name="planToBuildReserveDetails"
            as={
              <TextField
                label={fieldLabels.furtherInformation}
                readOnly={disabled}
                data-test="planToBuildReserveDetails-field"
                rows={8}
                multiline
              />
            }
          />
          {errors.planToBuildReserveDetails?.type === "maxLength" && (
            <Error data-test="planToBuildReserveDetails-validation">
              Must be no more than 5,000 characters
            </Error>
          )}
        </Grid>
      )}
    </>
  );
};
